import React, { useState, useEffect, useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shadcn-in-nx/ui/select";
import { Loader2, Search, Gauge } from "lucide-react";
import UsageCard from "./UsageCard";
import CompanyInfo from "./CompanyInfo";
import OverridePanel from "./OverridePanel";
import UsageLimitBypassPanel from "./UsageLimitBypassPanel";
import UserLimitBypassPanel from "./UserLimitBypassPanel";

export default function AdminAutomationUsage() {
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState("");
    const [search, setSearch] = useState("");
    const [usageData, setUsageData] = useState(null);

    const { call: fetchCompanies, isLoading: loadingCompanies } = useBluelagoon({ method: "getCompaniesForCredits" });
    const { call: fetchUsage, isLoading: loadingUsage } = useBluelagoon({ method: "getAutomationUsage" });

    const loadCompanies = useCallback(async (searchTerm) => {
        try {
            const res = await fetchCompanies({ limit: 100, search: searchTerm || undefined });
            if (res?.data) setCompanies(res.data);
        } catch (err) {
            console.error("Failed to fetch companies:", err);
        }
    }, [fetchCompanies]);

    useEffect(() => {
        const timer = setTimeout(() => loadCompanies(search), 300);
        return () => clearTimeout(timer);
    }, [search, loadCompanies]);

    const loadUsage = useCallback(async () => {
        if (!selectedCompany) return;
        try {
            const res = await fetchUsage({ companyId: selectedCompany });
            if (res?.data) setUsageData(res.data);
        } catch (err) {
            console.error("Failed to fetch usage:", err);
        }
    }, [selectedCompany, fetchUsage]);

    useEffect(() => {
        setUsageData(null);
        loadUsage();
    }, [selectedCompany, loadUsage]);

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center gap-2 mb-4">
                <Gauge className="h-6 w-6" />
                <h1 className="text-2xl font-bold">Company Settings</h1>
            </div>

            <div className="space-y-3 max-w-md">
                <div className="space-y-2">
                    <Label>Search Company</Label>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, ID, or email..." className="pl-9" />
                    </div>
                </div>
                <div className="space-y-2">
                    <Label>Company</Label>
                    <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                        <SelectTrigger>
                            <SelectValue placeholder={loadingCompanies ? "Loading..." : "Select company"} />
                        </SelectTrigger>
                        <SelectContent>
                            {companies.map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                    {c.name} ({c.currentCredits.toFixed(0)} credits)
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {loadingUsage && (
                <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading usage data...
                </div>
            )}

            {usageData && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <CompanyInfo data={usageData} />
                    <UsageCard data={usageData} />
                    <OverridePanel
                        companyId={usageData.companyId}
                        companyName={usageData.companyName}
                        currentOverride={usageData.limits.overrideLimit}
                        onUpdated={loadUsage}
                    />
                    <UsageLimitBypassPanel
                        companyId={usageData.companyId}
                        companyName={usageData.companyName}
                        currentBypass={!!usageData.limits.usageLimitBypass}
                        onUpdated={loadUsage}
                    />
                    <UserLimitBypassPanel
                        companyId={usageData.companyId}
                        companyName={usageData.companyName}
                        currentBypass={!!usageData.limits.userLimitBypass}
                        onUpdated={loadUsage}
                    />
                </div>
            )}
        </div>
    );
}
